import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Checkbox } from "@app/components/ui/checkbox";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import API from "@app/services/api";
import { Trans, useLingui } from "@lingui/macro";

interface PromptStack {
  id: string;
  title: string;
  model: string | null;
}

interface OntologyStack {
  id: string;
  title: string;
  comment: string | null;
  field_count?: number;
}

interface MetricsProfile {
  id: string;
  name: string;
  description: string | null;
  specialite: string | null;
  is_system: boolean;
  derived_from_ontology_stack_id: string | null;
  derived_from_dataset_id: string | null;
  entry_count?: number;
}

export default function ExperimentNewPage() {
  const { t } = useLingui();
  const { id: datasetId } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Offered to the LLM-as-judge pass only: a projection run calls no extraction model.
  const [promptStacks, setPromptStacks] = useState<PromptStack[]>([]);
  const [ontologyStackId, setOntologyStackId] = useState<string>("");
  const [ontologyStacks, setOntologyStacks] = useState<OntologyStack[]>([]);
  const [metricsProfileId, setMetricsProfileId] = useState<string>("");
  const [metricsProfiles, setMetricsProfiles] = useState<MetricsProfile[]>([]);
  const [enableLlmJudge, setEnableLlmJudge] = useState(false);
  // Date clinique par défaut de l'expérience : les lignes NON datées du jeu de données
  // sont alors évaluées à ce jour-là. Une ligne datée garde sa propre date.
  const [asOf, setAsOf] = useState("");
  const [judgePromptStackId, setJudgePromptStackId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPromptStacks();
    loadOntologyStacks();
    loadMetricsProfiles();
  }, []);

  const loadPromptStacks = async () => {
    try {
      const response = await API.get({ path: "/prompt-stack/" });
      if (response.ok && response.data?.promptStacks) {
        setPromptStacks(response.data.promptStacks);
      }
    } catch (err) {
      console.error("Error loading prompt stacks:", err);
    }
  };

  const loadOntologyStacks = async () => {
    try {
      const response = await API.get({ path: "/ontology-stack/" });
      if (response.ok && Array.isArray(response.data)) {
        setOntologyStacks(response.data as OntologyStack[]);
      }
    } catch (err) {
      console.error("Error loading ontology stacks:", err);
    }
  };

  const loadMetricsProfiles = async () => {
    try {
      const response = await API.get({ path: "/metrics-profile/" });
      if (response.ok && Array.isArray(response.data)) {
        setMetricsProfiles(response.data as MetricsProfile[]);
      }
    } catch (err) {
      console.error("Error loading metrics profiles:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t`Le nom est requis`);
      return;
    }

    if (enableLlmJudge && !judgePromptStackId) {
      setError(
        t`Sélectionnez une configuration LLM pour la passe LLM-as-judge.`,
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
        // The only surface left: the dated journal replayed.
        scored_surface: "PROJECTION",
      };
      if (ontologyStackId) {
        body.ontology_stack_id = ontologyStackId;
      }
      if (metricsProfileId) {
        body.metrics_profile_id = metricsProfileId;
      }
      if (enableLlmJudge) {
        body.enable_llm_judge = true;
        body.judge_prompt_stack_id = judgePromptStackId;
      }
      if (asOf) {
        body.as_of = asOf;
      }

      const response = await API.post({
        path: `/dataset/${datasetId}/experiments`,
        body,
      });

      if (response.ok && response.data) {
        navigate(`/datasets/${datasetId}/experiments/${response.data.id}`);
      } else {
        setError(response.error || t`Erreur lors de la création`);
      }
    } catch (err) {
      setError(t`Erreur lors de la création de l'expérience`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Link
        to={`/datasets/${datasetId}`}
        className="text-sm text-primary hover:underline"
      >
        <Trans>← Retour au jeu de données</Trans>
      </Link>

      <h1 className="text-3xl font-bold mt-4 mb-6"><Trans>Nouvelle Expérience</Trans></h1>

      <Card>
        <CardHeader>
          <CardTitle><Trans>Créer une expérience</Trans></CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name"><Trans>Nom *</Trans></Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t`Ex. : Test modèle v2`}
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="description"><Trans>Description</Trans></Label>
              <textarea
                id="description"
                className="w-full mt-1 border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t`Description optionnelle...`}
                rows={3}
                disabled={loading}
              />
            </div>

            <div>
              <Label><Trans>Configuration d'ontologie</Trans></Label>
              <Select
                value={ontologyStackId || "__default__"}
                onValueChange={(v) =>
                  setOntologyStackId(v === "__default__" ? "" : v)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t`Sélectionner une configuration...`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">
                    <Trans>Aucune (champs du jeu de données)</Trans>
                  </SelectItem>
                  {ontologyStacks.map((os) => (
                    <SelectItem key={os.id} value={os.id}>
                      <span className="flex items-center gap-2">
                        {os.title}
                        {os.field_count !== undefined && (
                          <span className="text-slate-400 text-xs">
                            ({os.field_count} <Trans>champs</Trans>)
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                <Trans>La configuration d'ontologie définit les champs évalués.</Trans>
              </p>
            </div>

            <div>
              <Label><Trans>Profil de métriques</Trans></Label>
              <Select
                value={metricsProfileId || "__auto__"}
                onValueChange={(v) =>
                  setMetricsProfileId(v === "__auto__" ? "" : v)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t`Contrat par défaut (ontologie de la spécialité)`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">
                    <Trans>Contrat par défaut (ontologie de la spécialité)</Trans>
                  </SelectItem>
                  {metricsProfiles.map((mp) => (
                    <SelectItem key={mp.id} value={mp.id}>
                      <span className="flex items-center gap-2">
                        {mp.name}
                        {mp.is_system && (
                          <span className="text-slate-400 text-xs">
                            (<Trans>système</Trans>)
                          </span>
                        )}
                        {mp.entry_count !== undefined && (
                          <span className="text-slate-400 text-xs">
                            ({mp.entry_count} <Trans>champs</Trans>)
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                <Trans>
                  Par défaut, chaque champ est scoré avec l'évaluateur et le poids
                  déclarés dans l'ontologie. Un profil les remplace : seuls ses
                  champs sont alors scorés.
                </Trans>
              </p>
            </div>

            <div>
              <Label htmlFor="as-of">
                <Trans>Date clinique (optionnel)</Trans>
              </Label>
              <Input
                id="as-of"
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                disabled={loading}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                <Trans>
                  Reproduit l'état des patients à ce jour-là : la projection ne
                  retient que les documents datés d'avant, et la note est calculée
                  sur la valeur validée pour ce jour-là. Les lignes du jeu de
                  données qui portent déjà leur propre date gardent la leur. Vide
                  = tous les documents.
                </Trans>
              </p>
            </div>

            <div className="flex flex-col gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="enable-llm-judge"
                  checked={enableLlmJudge}
                  onCheckedChange={(v) => setEnableLlmJudge(v === true)}
                  disabled={loading}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="enable-llm-judge"
                    className="cursor-pointer font-medium"
                  >
                    <Trans>
                      Activer LLM-as-judge sur les champs textuels libres
                    </Trans>
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    <Trans>
                      Évalue aussi les champs `string` et `array_string` via
                      la configuration LLM sélectionnée ci-dessous, en plus du
                      score déterministe. Plus pertinent pour les textes libres
                      où l'exact-match est trop strict, mais ralentit
                      l'expérience de quelques secondes par patient.
                    </Trans>
                  </p>
                </div>
              </div>

              {enableLlmJudge && (
                <div className="pl-7">
                  <Label htmlFor="judge-prompt-stack">
                    <Trans>Configuration LLM-as-judge *</Trans>
                  </Label>
                  <Select
                    value={judgePromptStackId || "__none__"}
                    onValueChange={(v) =>
                      setJudgePromptStackId(v === "__none__" ? "" : v)
                    }
                  >
                    <SelectTrigger id="judge-prompt-stack" className="mt-1">
                      <SelectValue
                        placeholder={t`Sélectionner une configuration...`}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        <Trans>Aucune sélection</Trans>
                      </SelectItem>
                      {promptStacks.map((ps) => (
                        <SelectItem key={ps.id} value={ps.id}>
                          <span className="flex items-center gap-2">
                            {ps.title}
                            {ps.model && (
                              <span className="text-slate-400 text-xs">
                                ({ps.model})
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    <Trans>
                      Modèle utilisé uniquement pour la passe d'évaluation
                      sémantique — indépendant du modèle d'extraction.
                    </Trans>
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/datasets/${datasetId}`)}
                disabled={loading}
              >
                <Trans>Annuler</Trans>
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? t`Création...` : t`Créer l'expérience`}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
